import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';

import SectionLayout from '../../SectionLayout';
import { GroupAdmin, UserOperationType, UserType } from '../../types';
import UsersTable from '../UsersTable';
import UserRow from '../UsersTable/UserRow';

interface AdminsProps {
  admins: GroupAdmin[];
  onUserClick: (user: GroupAdmin, operation: UserOperationType) => void;
}

export const ADMIN_PLACEHOLDER: GroupAdmin = {
  id: -1,
  name: 'Admin name',
  email: 'Admin email',
  type: UserType.Admin,
};

const normalizeAdminUsers = (users: GroupAdmin[]) => {
  if (users.length < 2) {
    return [...users, ADMIN_PLACEHOLDER];
  }

  return users;
};

const Admins = ({ admins, onUserClick }: AdminsProps) => {
  const maxAdmins = Math.max(admins.length, 2);
  const displayAdmins = normalizeAdminUsers(admins);
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Admins,
  });

  const handleUserClick = (user: GroupAdmin, operation: UserOperationType) => {
    onUserClick(user, operation);
    trackEvent(
      click({
        clickTarget: ClickTarget.UserRow,
        contentId: user.id,
        contentType: ContentType.User,
      })
    );
  };

  return (
    <SectionLayout
      id="admins"
      title="Admins"
      sectionId="admin-users"
      subtitles={[`Administration accounts: ${admins.length}/${maxAdmins}`]}
      note="By default, admins do not have user access. If you would like access, please add yourself as a user."
    >
      <UsersTable userType={UserType.Admin}>
        {displayAdmins.map((admin) => (
          <UserRow key={admin.id} user={admin} onUserClick={handleUserClick} />
        ))}
      </UsersTable>
    </SectionLayout>
  );
};

export default Admins;
